export const AI_MODEL_MANAGER_CATEGORIES = [
  "Accessories",
  "Bottoms",
  "Full Body",
  "Headwear",
  "Intimates",
  "Outerwear",
  "Sets & Suits",
  "Tops",
] as const;

export type AiModelManagerCategory =
  (typeof AI_MODEL_MANAGER_CATEGORIES)[number];

export type PredictionJobStatus =
  | "queued"
  | "processing"
  | "failed"
  | "completed";

export type PredictionResultRow = {
  rank_input_order?: number;
  rank_share_order?: number;
  item_name: string;
  category: string;
  price: number;
  predicted_share: number;
  predicted_share_pct: number;
};

export type PredictionOutput = {
  success?: boolean;
  error?: string;
  item_count?: number;
  share_sum?: number;
  results?: PredictionResultRow[];
};

export type PredictionJobResponse = {
  job_id: string;
  status: PredictionJobStatus;
  created_by?: string | null;
  celery_task_id?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  request_payload?: Record<string, unknown>;
  result_payload?: Record<string, unknown>;
  error_payload?: Record<string, unknown>;
};

export type PredictionSubmitItemInput = {
  item_name: string;
  caption: string;
  price: number;
  category: AiModelManagerCategory;
  original_filename: string;
  imageFile: File;
};

export type PredictionHistoryEntry = {
  generation_id: string;
  source_job_id: string;
  generated_at: string;
  generated_at_local: string;
  generated_at_local_label: string;
  created_by?: string | null;
  prompt_hash?: string | null;
  runpod_job_id?: string | null;
  item_count?: number | null;
  share_sum?: number | null;
};

export type PredictionHistoryDetail = PredictionHistoryEntry & {
  request_payload?: Record<string, unknown>;
  result_payload?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  items?: Array<Record<string, unknown>>;
};

type PredictionSubmitPayload = {
  clientCorrelationId?: string;
  items: PredictionSubmitItemInput[];
};

const API_BASE = "/v1/api/ai-model-manager/predictions";

export async function submitPredictionJob(
  payload: PredictionSubmitPayload
): Promise<PredictionJobResponse> {
  const formData = buildPredictionFormData(payload);
  const response = await fetch(`${API_BASE}/submit/`, {
    method: "POST",
    body: formData,
  });

  return parseJsonResponse<PredictionJobResponse>(response);
}

export async function getPredictionJob(
  jobId: string
): Promise<PredictionJobResponse> {
  const response = await fetch(`${API_BASE}/${jobId}/`, {
    method: "GET",
  });

  return parseJsonResponse<PredictionJobResponse>(response);
}

export async function listPredictionHistory(
  options: { days?: number; limit?: number } = {}
): Promise<PredictionHistoryEntry[]> {
  const params = new URLSearchParams();
  if (typeof options.days === "number") {
    params.set("days", String(options.days));
  }
  if (typeof options.limit === "number") {
    params.set("limit", String(options.limit));
  }

  const query = params.toString();
  const response = await fetch(
    `${API_BASE}/history/${query ? `?${query}` : ""}`,
    {
      method: "GET",
    }
  );
  const payload = await parseJsonResponse<{ results?: PredictionHistoryEntry[] }>(
    response
  );
  return Array.isArray(payload.results) ? payload.results : [];
}

export async function getPredictionHistoryDetail(
  generationId: string
): Promise<PredictionHistoryDetail> {
  const response = await fetch(`${API_BASE}/history/${generationId}/`, {
    method: "GET",
  });
  return parseJsonResponse<PredictionHistoryDetail>(response);
}

export async function pollPredictionJob(
  jobId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
    onUpdate?: (job: PredictionJobResponse) => void;
  } = {}
): Promise<PredictionJobResponse> {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 15 * 60 * 1000;
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    throwIfAborted(options.signal);

    const job = await getPredictionJob(jobId);
    options.onUpdate?.(job);

    if (job.status === "completed" || job.status === "failed") {
      return job;
    }

    await delay(intervalMs, options.signal);
  }

  throw new Error("Prediction job polling timed out.");
}

export function getPredictionOutput(
  job: PredictionJobResponse | null
): PredictionOutput | null {
  return getPredictionOutputFromResultPayload(job?.result_payload);
}

export function getPredictionOutputFromResultPayload(
  resultPayload?: Record<string, unknown> | null
): PredictionOutput | null {
  if (!resultPayload) {
    return null;
  }

  const runpod = asRecord(resultPayload.runpod);
  const statusResponse = asRecord(runpod?.status_response);
  const output = asRecord(statusResponse?.output);

  if (output) {
    return {
      success:
        typeof output.success === "boolean" ? output.success : undefined,
      error: typeof output.error === "string" ? output.error : undefined,
      item_count:
        typeof output.item_count === "number" ? output.item_count : undefined,
      share_sum:
        typeof output.share_sum === "number" ? output.share_sum : undefined,
      results: Array.isArray(output.results)
        ? (output.results as PredictionResultRow[])
        : undefined,
    };
  }

  return null;
}

function buildPredictionFormData(payload: PredictionSubmitPayload): FormData {
  const formData = new FormData();

  if (payload.clientCorrelationId) {
    formData.append("client_correlation_id", payload.clientCorrelationId);
  }

  const serializedItems = payload.items.map((item) => ({
    item_name: item.item_name,
    caption: item.caption,
    price: item.price,
    category: item.category,
    original_filename: item.original_filename,
  }));

  formData.append("items", JSON.stringify(serializedItems));

  for (const item of payload.items) {
    formData.append("images", item.imageFile);
  }

  return formData;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as T) : ({} as T);

  if (!response.ok) {
    const message = extractErrorMessage(payload);
    throw new Error(message || `Request failed with status ${response.status}.`);
  }

  return payload;
}

function extractErrorMessage(payload: unknown): string | null {
  if (typeof payload === "string") {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const errorRecord = payload as Record<string, unknown>;

  if (typeof errorRecord.detail === "string") {
    return errorRecord.detail;
  }

  const firstValue = Object.values(errorRecord)[0];
  if (typeof firstValue === "string") {
    return firstValue;
  }

  if (Array.isArray(firstValue) && typeof firstValue[0] === "string") {
    return firstValue[0];
  }

  return null;
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    function onAbort() {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    }

    function cleanup() {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
    }

    signal?.addEventListener("abort", onAbort);
  });
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}
